import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  Business,
  OutboundReferral,
  CreateOutboundReferralInput,
  OutboundReferralStatus,
  PartnerOutboundReferralView,
} from '@/types';

const functions = getFunctions();

// ===========================================
// CRM User Functions
// ===========================================

interface CreateReferralOutput {
  success: boolean;
  referralId?: string;
  error?: string;
}

interface ListReferralsInput {
  companyId?: string;
  business?: Business;
  limit?: number;
}

interface ListReferralsOutput {
  success: boolean;
  referrals?: OutboundReferral[];
  error?: string;
}

/**
 * Create a new outbound referral (send a job to a partner)
 */
export async function createOutboundReferral(
  input: CreateOutboundReferralInput
): Promise<CreateReferralOutput> {
  const create = httpsCallable<CreateOutboundReferralInput, CreateReferralOutput>(
    functions,
    'crm_createOutboundReferral'
  );

  const result = await create(input);
  return result.data;
}

/**
 * List outbound referrals, optionally filtered by company or business
 */
export async function listOutboundReferrals(
  companyId?: string,
  business?: Business,
  limit: number = 50
): Promise<ListReferralsOutput> {
  const list = httpsCallable<ListReferralsInput, ListReferralsOutput>(
    functions,
    'crm_listOutboundReferrals'
  );

  const result = await list({ companyId, business, limit });
  return result.data;
}

// ===========================================
// Partner Portal Functions
// ===========================================

interface GetPartnerReferralsInput {
  sessionToken: string;
}

interface GetPartnerReferralsOutput {
  success: boolean;
  referrals?: PartnerOutboundReferralView[];
  error?: string;
}

interface UpdateStatusInput {
  sessionToken: string;
  referralId: string;
  status: OutboundReferralStatus;
  statusNotes?: string;
}

interface UpdateStatusOutput {
  success: boolean;
  error?: string;
}

/**
 * Get outbound referrals for the current partner (partner portal)
 */
export async function getPartnerOutboundReferrals(
  sessionToken: string
): Promise<GetPartnerReferralsOutput> {
  const get = httpsCallable<GetPartnerReferralsInput, GetPartnerReferralsOutput>(
    functions,
    'crm_getPartnerOutboundReferrals'
  );

  const result = await get({ sessionToken });
  return result.data;
}

/**
 * Update the status of an outbound referral (partner portal)
 */
export async function updateOutboundReferralStatus(
  sessionToken: string,
  referralId: string,
  status: OutboundReferralStatus,
  statusNotes?: string
): Promise<UpdateStatusOutput> {
  const update = httpsCallable<UpdateStatusInput, UpdateStatusOutput>(
    functions,
    'crm_updateOutboundReferralStatus'
  );

  const result = await update({ sessionToken, referralId, status, statusNotes });
  return result.data;
}
